import { ServerResponse, IncomingMessage } from 'http';

export interface RequestContext {
  req: IncomingMessage;
  userId?: string;
  body: Record<string, unknown>;
  params: Record<string, string>;
  query: Record<string, string>;
}

/**
 * Standardized error handler for service layer errors.
 * Logs detailed error information server-side while sending a safe, contextual message to the client.
 * 
 * @param err - The caught error object (unknown type for maximum compatibility)
 * @param res - Server response object to send the error response
 * @param ctx - Request context containing user and request information for logging
 * @param options - Configuration for logging and client response
 * @param options.category - Log category (e.g., 'page', 'admin', 'notifications')
 * @param options.logMessage - Internal log message describing the error context
 * @param options.clientMessage - User-facing error message (should be safe and helpful)
 * @param options.context - Additional context data to include in server logs
 * 
 * @example
 * try {
 *   await pageService.updatePageConfig(userId, data);
 * } catch (err) {
 *   return handleServiceError(err, res, ctx, {
 *     category: 'page',
 *     logMessage: 'Error updating page config',
 *     clientMessage: 'Failed to update page configuration',
 *     context: { body: ctx.body },
 *   });
 * }
 */
export function handleServiceError(
  err: unknown,
  res: ServerResponse,
  ctx: RequestContext,
  options: {
    category: string;
    logMessage: string;
    clientMessage: string;
    context?: Record<string, unknown>;
  }
): void {
  const { category, logMessage, clientMessage, context = {} } = options;

  import('./logger.js').then(({ logger }) => {
    logger.error(category, logMessage, {
      error: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
      userId: ctx.userId,
      ...context,
    });
  });

  const errorResponse = {
    success: false,
    error: clientMessage,
    status: 500,
  };

  res.writeHead(500, {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': ctx.req.headers.origin || '*',
    'Access-Control-Allow-Methods': 'GET, POST, PATCH, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization',
  });
  res.end(JSON.stringify(errorResponse));
}

/**
 * Handle expected validation and business logic errors from services.
 * Used when services throw intentional errors (e.g., "Invalid email", "User already exists").
 * These are 4xx errors where the error message is safe to show to users.
 * 
 * @param err - The caught error object from service layer
 * @param res - Server response object
 * @param ctx - Request context
 * @param statusCode - HTTP status code (default 400 for validation errors)
 * 
 * @example
 * try {
 *   const link = await linkService.createLink(userId, data);
 * } catch (err) {
 *   return handleValidationError(err, res, ctx);
 * }
 */
export function handleValidationError(
  err: unknown,
  res: ServerResponse,
  ctx: RequestContext,
  statusCode: number = 400
): void {
  const message = err instanceof Error ? err.message : String(err);
  const errorResponse = {
    success: false,
    error: message,
    status: statusCode,
  };

  res.writeHead(statusCode, {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': ctx.req.headers.origin || '*',
    'Access-Control-Allow-Methods': 'GET, POST, PATCH, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization',
  });
  res.end(JSON.stringify(errorResponse));
}

/**
 * Check if user is authenticated and send 401 error if not.
 * Reduces boilerplate auth checking across routes.
 * 
 * @param ctx - Request context containing userId
 * @param res - Server response object
 * @returns true if authenticated, false if not (already sent error response)
 * 
 * @example
 * if (!requireAuth(ctx, res)) return;
 * // Continue with authenticated logic
 */
export function requireAuth(ctx: RequestContext, res: ServerResponse): boolean {
  if (!ctx.userId) {
    const errorResponse = {
      success: false,
      error: 'Not authenticated',
      status: 401,
    };
    res.writeHead(401, {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': ctx.req.headers.origin || '*',
      'Access-Control-Allow-Methods': 'GET, POST, PATCH, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Content-Type, Authorization',
    });
    res.end(JSON.stringify(errorResponse));
    return false;
  }
  return true;
}

/**
 * Parse integer from query parameter with optional default value.
 * Handles undefined, invalid numbers, and provides type safety.
 * 
 * @param value - Query parameter value (string | undefined)
 * @param defaultValue - Default value if parsing fails or value is undefined
 * @returns Parsed integer or default value
 * 
 * @example
 * const limit = parseIntQuery(ctx.query.limit, 50); // returns 50 if not provided
 * const offset = parseIntQuery(ctx.query.offset, 0); // returns 0 if not provided
 * const size = parseIntQuery(ctx.query.size); // returns undefined if not provided
 */
export function parseIntQuery(value: string | undefined, defaultValue?: number): number | undefined {
  if (value === undefined || value === '') {
    return defaultValue;
  }
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Validate that required fields are present in an object.
 * Returns error message if any field is missing or falsy.
 * 
 * @param data - Object containing the fields to validate
 * @param fieldNames - Array of required field names
 * @returns Error message string if validation fails, null if all fields present
 * 
 * @example
 * const error = validateRequired(ctx.body, ['email', 'password', 'name']);
 * if (error) return error(res, error, 400, ctx.req);
 */
export function validateRequired(
  data: Record<string, unknown>,
  fieldNames: string[]
): string | null {
  const missing = fieldNames.filter(field => !data[field]);

  if (missing.length === 0) {
    return null;
  }

  if (missing.length === 1) {
    return `${missing[0]} is required`;
  }

  const lastField = missing.pop()!;
  return `${missing.join(', ')}${missing.length > 0 ? ', and ' : ''}${lastField} are required`;
}

/**
 * Sanitize a plain-text field (name, email) by stripping control characters and truncating.
 * Does NOT strip HTML tags — tag stripping on plain-text fields would corrupt angle-bracket
 * content that is not HTML.
 */
export function sanitizePlainText(value: unknown, maxLength: number): string {
  if (typeof value !== 'string') return '';
  const normalized = value.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim();
  return normalized.slice(0, maxLength);
}

/**
 * Sanitize a free-form message body by stripping only true control characters.
 * Intentionally does NOT strip HTML tags — the message is delivered as plain text,
 * so angle-bracket characters are legitimate (e.g. code snippets, comparisons).
 */
export function sanitizeMessageBody(value: unknown, maxLength: number): string {
  if (typeof value !== 'string') return '';
  const withoutControls = value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
  return withoutControls.trim().slice(0, maxLength);
}

/**
 * Validate an email address format. Length must be checked separately before
 * calling this function to avoid silent truncation masking overlong addresses.
 */
export function isValidEmailAddress(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
